import { useState } from "react";
import { Building2, Play } from "lucide-react";
import type { Demo } from "@/api/demos";
import { fileSize } from "@/api/demos";
import { DemoThumb } from "./DemoMedia";
import { DemoViewer } from "./DemoDialogs";

const KIND_LABEL: Record<Demo["kind"], string> = {
  video: "Video",
  audio: "Audio",
  image: "Image",
  pdf: "PDF",
  other: "File",
};

/** Cards for a set of demos; clicking one opens the player. */
export default function DemoGrid({ demos }: { demos: Demo[] }) {
  const [open, setOpen] = useState<Demo | null>(null);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {demos.map((d) => (
          <button
            key={d.id}
            onClick={() => setOpen(d)}
            className="group panel overflow-hidden text-left transition-shadow hover:shadow-soft"
          >
            <div className="relative aspect-video overflow-hidden bg-ink-800">
              <DemoThumb demo={d} />
              {(d.kind === "video" || d.kind === "audio") && (
                <span className="absolute inset-0 grid place-items-center bg-black/0 transition-colors group-hover:bg-black/25">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-white/90 text-ink-50 opacity-90 shadow-soft transition-transform group-hover:scale-105">
                    <Play className="ml-0.5 h-5 w-5 fill-current text-[#101426]" />
                  </span>
                </span>
              )}
              <span className="absolute left-2 top-2 rounded-md bg-black/55 px-1.5 py-0.5 text-2xs font-semibold text-white">
                {KIND_LABEL[d.kind]}
              </span>
            </div>
            <div className="space-y-1.5 p-4">
              <div className="truncate text-md font-semibold">{d.title}</div>
              <div className="flex items-center gap-2 text-xs text-mut-500">
                {d.product && <span className="truncate text-accent-violet">{d.product}</span>}
                <span>{fileSize(d.size_bytes)}</span>
              </div>
              {d.accounts.length > 0 && (
                <div className="flex items-center gap-1 truncate text-xs text-mut-300">
                  <Building2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">{d.accounts.map((a) => a.name).join(", ")}</span>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      {open && <DemoViewer demo={open} onClose={() => setOpen(null)} />}
    </>
  );
}
